import { Model } from '../../types/mfc'
import {
    getStateUser,
    getUser,
    resolveID,
    resolveSlug,
} from '../state/state.users'
import { loadUser } from './loadUser'
import { socketLookupUser } from './socketLookupUser'

/**
 * Calls `socketLookupUser` falling back to `loadUser` (API lookup) if the user is not found.
 * @param identifier - username or user ID
 * @param next - callback to be called on success. callback is passed the current slug,
 * which may be different from `identifier` (possibly if the user has changed their username).
 * @returns promise resolving to true on success
 */
export async function loadUserSocket(
    identifier: string | number,
    next?: (currentSlug: string) => any,
) {
    const normalizedIdentifier =
        typeof identifier === 'string' ? identifier.toLowerCase() : identifier
    const existingUser = getUser(identifier)
    if (existingUser) {
        return loadUserSuccess(existingUser, next)
    }
    const result = await socketLookupUser(normalizedIdentifier)
    if (result) {
        return loadUserSuccess(
            { exists: result, identifier: normalizedIdentifier },
            next,
        )
    }
    console.warn(`${identifier}: lookup results empty, trying fallback (api)`)
    const exists = await loadUser(normalizedIdentifier)
    if (!exists) {
        console.warn(`${identifier}: lookup results from api are empty`)
        return loadUserSuccess(null, next)
    }
    const user = getStateUser(resolveID(identifier))
    return loadUserSuccess(user, next)
}

type Result = {
    exists: boolean
    identifier: string | number
}

function isExistingUser(user: Model | Result): user is Model {
    return (user as Model)?.id !== undefined
}

function loadUserSuccess(user?: Model | Result, next?: (slug: string) => any) {
    if (typeof user !== 'object' || user === null) {
        return false
    }
    if (!isExistingUser(user) && !user.exists) {
        return false
    }
    if (next) {
        const slug = !isExistingUser(user)
            ? resolveSlug(user.identifier)
            : user.slug
        next(slug)
    }
    return true
}
